import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';

interface FormSliderComponentInterface {
	Element: HTMLDivElement;
	Args: {
		min?: number;
		name?: string;
		max?: number;
		step?: number;
		value?: number | string;
		onChange: (value: number) => void;
	};
}

export default class FormSliderComponent extends Component<FormSliderComponentInterface> {
	min;
	max;
	step;
	name;
	@tracked value;
	contentNode: HTMLDivElement | null = null;

	constructor(owner: Owner, args: FormSliderComponentInterface['Args']) {
		super(owner, args);
		this.min = this.args.min ?? 0;
		this.max = this.args.max ?? 100;
		this.step = this.args.step ?? 1;
		this.value = Number(this.args.value ?? 50);
		this.name = this.args.name ?? undefined;
	}

	get percentage() {
		return Math.floor(((this.value - this.min) * 100) / (this.max - this.min));
	}

	@action
	updateSliderStyles() {
		if (this.contentNode) {
			const slider = this.contentNode.querySelector('input')!,
				output = this.contentNode.querySelector('output')!,
				// Sorta magic numbers based on size of the native UI thumb
				newPosition = 11 - this.percentage * 0.175;

			this.contentNode.setAttribute('data-before', String(this.min));
			this.contentNode.setAttribute('data-after', String(this.max));

			slider.style.setProperty('background-size', `${this.percentage}% 100%`);
			output.style.left = `calc(${this.percentage}% + (${newPosition}px))`;
		}
	}

	@action
	onChange(event: Event) {
		this.value = +(event.target as HTMLInputElement).value;
		this.updateSliderStyles();
		this.args.onChange(this.value);
	}

	@action
	setupView(node: HTMLElement) {
		this.contentNode = node as HTMLDivElement;
		this.updateSliderStyles();
	}

	// keep internal value in sync when `@value` changes from outside (e.g. a linked dropdown)
	@action
	onValueArgUpdate() {
		const newValue = +(this.args.value ?? 50);
		if (newValue !== this.value) {
			this.value = newValue;
			this.updateSliderStyles();
		}
	}

	<template>
		<div
			class="slider-container"
			{{didInsert this.setupView}}
			{{didUpdate this.onValueArgUpdate @value}}
		>
			<output class="slider-value">{{this.value}}</output>
			<input
				name={{this.name}}
				type="range"
				min={{this.min}}
				max={{this.max}}
				step={{this.step}}
				value={{this.value}}
				class="slider"
				{{! @glint-expect-error: it's probably fine :P }}
				oninput={{this.onChange}}
			/>
		</div>
	</template>
}
